import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Search, Loader2, Upload, CheckCircle, AlertCircle, Package, Edit, Eye, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorProductsAdmin() {
    const { token } = useAuth();
    const [products, setProducts] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVendor, setSelectedVendor] = useState('all');
    const [syncFilter, setSyncFilter] = useState('all'); // all, synced, not_synced
    
    // Selection for bulk actions
    const [selectedProducts, setSelectedProducts] = useState([]);
    
    // Price adjustment dialog
    const [editDialog, setEditDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [adjustedPrice, setAdjustedPrice] = useState('');
    
    // Sync preview dialog
    const [syncDialog, setSyncDialog] = useState(false);
    const [productsToSync, setProductsToSync] = useState([]);
    const [syncing, setSyncing] = useState(false);

    const fetchProducts = useCallback(async () => {
        if (!token) return; // Wait for token
        
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/vendor-products`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProducts(data);
                
                // Extract unique vendors
                const uniqueVendors = [...new Map(data.map(p => [p.vendeur_id, { id: p.vendeur_id, name: p.vendeur_name }])).values()];
                setVendors(uniqueVendors);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const filteredProducts = products.filter(p => {
        const matchesSearch = 
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.vendeur_name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesVendor = selectedVendor === 'all' || p.vendeur_id === selectedVendor;
        
        const matchesSync = syncFilter === 'all' || 
            (syncFilter === 'synced' && p.synced_to_shopify) ||
            (syncFilter === 'not_synced' && !p.synced_to_shopify);
        
        return matchesSearch && matchesVendor && matchesSync;
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-DZ', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0) + ' DA';
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedProducts(filteredProducts.map(p => p.id));
        } else {
            setSelectedProducts([]);
        }
    };

    const handleSelectProduct = (productId, checked) => {
        if (checked) {
            setSelectedProducts([...selectedProducts, productId]);
        } else {
            setSelectedProducts(selectedProducts.filter(id => id !== productId));
        }
    };

    const handleEditPrice = (product) => {
        setEditingProduct(product);
        setAdjustedPrice(product.prix_shopify?.toString() || product.prix?.toString() || '');
        setEditDialog(true);
    };

    const handleSavePrice = async () => {
        if (!adjustedPrice || parseFloat(adjustedPrice) <= 0) {
            toast.error('Le prix doit être supérieur à 0');
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/vendor-products/${editingProduct.id}/shopify-price`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ prix_shopify: parseFloat(adjustedPrice) })
            });

            if (res.ok) {
                toast.success('Prix Shopify mis à jour');
                setEditDialog(false);
                fetchProducts();
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Erreur');
            }
        } catch (error) {
            toast.error('Erreur réseau');
        }
    };

    const handleDeleteProduct = async (product) => {
        if (!window.confirm(`Supprimer "${product.name}" de ${product.vendeur_name} ?`)) return;

        try {
            const res = await fetch(`${API_URL}/api/vendor-products/${product.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Produit supprimé');
                fetchProducts();
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Erreur lors de la suppression');
            }
        } catch (error) {
            toast.error('Erreur réseau');
        }
    };

    const handlePreviewSync = () => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez au moins un produit');
            return;
        }
        
        const toSync = products.filter(p => selectedProducts.includes(p.id));
        setProductsToSync(toSync);
        setSyncDialog(true);
    };

    const handleSyncToShopify = async () => {
        setSyncing(true);
        try {
            const res = await fetch(`${API_URL}/api/vendor-products/sync-to-shopify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ product_ids: selectedProducts })
            });

            const result = await res.json();
            
            if (res.ok) {
                toast.success(`${result.synced} produit(s) synchronisé(s) vers Shopify`);
                setSyncDialog(false);
                setSelectedProducts([]);
                fetchProducts();
            } else {
                toast.error(result.detail || 'Erreur lors de la synchronisation');
            }
        } catch (error) {
            toast.error('Erreur réseau');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6 overflow-x-hidden" data-testid="vendor-products-admin-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Produits Vendeurs</h1>
                        <p className="text-muted-foreground mt-1 text-sm md:text-base">
                            Gérez et synchronisez les produits vendeurs vers Shopify
                        </p>
                    </div>
                    {selectedProducts.length > 0 && (
                        <Button 
                            onClick={handlePreviewSync} 
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="sync-to-shopify-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Sync vers Shopify ({selectedProducts.length})
                        </Button>
                    )}
                </div>

                {/* Filters */}
                <Card className="card-luxury">
                    <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            
                            {/* Vendor Filter */}
                            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Tous les vendeurs" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les vendeurs</SelectItem>
                                    {vendors.map(v => (
                                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            {/* Sync Status Filter */}
                            <Select value={syncFilter} onValueChange={setSyncFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Statut sync" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous</SelectItem>
                                    <SelectItem value="synced">✓ Synchronisés</SelectItem>
                                    <SelectItem value="not_synced">⏳ Non synchronisés</SelectItem>
                                </SelectContent>
                            </Select>
                            
                            {/* Stats */}
                            <div className="flex items-center gap-4 text-sm">
                                <span className="text-muted-foreground">
                                    {filteredProducts.length} produit(s)
                                </span>
                                <span className="text-green-600">
                                    {filteredProducts.filter(p => p.synced_to_shopify).length} sync
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Products Table */}
                <Card className="card-luxury">
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Package className="w-16 h-16 text-muted-foreground mb-4" />
                                <p className="text-lg font-medium text-muted-foreground">
                                    Aucun produit trouvé
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12">
                                                <Checkbox 
                                                    checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                                                    onCheckedChange={handleSelectAll}
                                                />
                                            </TableHead>
                                            <TableHead>Produit</TableHead>
                                            <TableHead>Vendeur</TableHead>
                                            <TableHead>Prix Vendeur</TableHead>
                                            <TableHead>Prix Shopify</TableHead>
                                            <TableHead>SKU</TableHead>
                                            <TableHead>Stock</TableHead>
                                            <TableHead>Statut</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredProducts.map((product) => (
                                            <TableRow key={product.id}>
                                                <TableCell>
                                                    <Checkbox 
                                                        checked={selectedProducts.includes(product.id)}
                                                        onCheckedChange={(checked) => handleSelectProduct(product.id, checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        {product.image_url ? (
                                                            <img 
                                                                src={product.image_url} 
                                                                alt={product.name}
                                                                className="w-10 h-10 rounded object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                                                                <Package className="w-5 h-5 text-slate-400" />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="font-medium truncate max-w-[200px]">{product.name}</p>
                                                            <p className="text-xs text-muted-foreground">{product.category}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm">{product.vendeur_name}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="font-medium">{formatCurrency(product.prix)}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`font-medium ${product.prix_shopify && product.prix_shopify !== product.prix ? 'text-blue-600' : ''}`}>
                                                        {formatCurrency(product.prix_shopify || product.prix)}
                                                    </span>
                                                    {product.prix_shopify && product.prix_shopify !== product.prix && (
                                                        <span className="text-xs text-muted-foreground ml-1">(ajusté)</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-muted-foreground">{product.sku || '-'}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`text-sm ${product.stock_quantity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                                        {product.stock_quantity || 0}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    {product.synced_to_shopify ? (
                                                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                                            <CheckCircle className="w-3 h-3" />
                                                            Sync
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                                                            <AlertCircle className="w-3 h-3" />
                                                            En attente
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleEditPrice(product)}
                                                            title="Ajuster prix Shopify"
                                                            data-testid={`edit-price-${product.id}`}
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                            onClick={() => handleDeleteProduct(product)}
                                                            title="Supprimer"
                                                            data-testid={`delete-${product.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Edit Price Dialog */}
            <Dialog open={editDialog} onOpenChange={setEditDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Ajuster le prix Shopify</DialogTitle>
                        <DialogDescription>
                            Définissez un prix différent pour Shopify (le prix vendeur reste inchangé)
                        </DialogDescription>
                    </DialogHeader>
                    {editingProduct && (
                        <div className="space-y-4 py-4">
                            <div className="p-3 bg-slate-50 rounded-lg">
                                <p className="font-medium">{editingProduct.name}</p>
                                <p className="text-sm text-muted-foreground">Vendeur: {editingProduct.vendeur_name}</p>
                                <p className="text-sm">Prix vendeur: <strong>{formatCurrency(editingProduct.prix)}</strong></p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="prix_shopify">Prix Shopify (DA)</Label>
                                <Input
                                    id="prix_shopify"
                                    type="number"
                                    value={adjustedPrice}
                                    onChange={(e) => setAdjustedPrice(e.target.value)}
                                    placeholder="Prix pour Shopify"
                                />
                            </div>
                            
                            {adjustedPrice && editingProduct.prix && (
                                <div className={`p-3 rounded-lg ${parseFloat(adjustedPrice) > editingProduct.prix ? 'bg-green-50' : 'bg-amber-50'}`}>
                                    <p className={`text-sm ${parseFloat(adjustedPrice) > editingProduct.prix ? 'text-green-700' : 'text-amber-700'}`}>
                                        {parseFloat(adjustedPrice) > editingProduct.prix ? (
                                            <>Marge ajoutée: <strong>+{formatCurrency(parseFloat(adjustedPrice) - editingProduct.prix)}</strong></>
                                        ) : parseFloat(adjustedPrice) < editingProduct.prix ? (
                                            <>⚠️ Prix inférieur au vendeur: <strong>{formatCurrency(editingProduct.prix - parseFloat(adjustedPrice))}</strong></>
                                        ) : (
                                            <>Prix identique au vendeur</>
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialog(false)}>Annuler</Button>
                        <Button onClick={handleSavePrice}>Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sync Preview Dialog */}
            <Dialog open={syncDialog} onOpenChange={setSyncDialog}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Confirmer la synchronisation Shopify</DialogTitle>
                        <DialogDescription>
                            Les produits suivants seront créés/mis à jour dans votre boutique Shopify
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="border rounded-lg overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Produit</TableHead>
                                        <TableHead>Prix Shopify</TableHead>
                                        <TableHead>SKU</TableHead>
                                        <TableHead>Stock</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {productsToSync.map((product) => (
                                        <TableRow key={product.id}>
                                            <TableCell>
                                                <p className="font-medium">{product.name}</p>
                                                <p className="text-xs text-muted-foreground">{product.vendeur_name}</p>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-medium">{formatCurrency(product.prix_shopify || product.prix)}</span>
                                            </TableCell>
                                            <TableCell>{product.sku || '-'}</TableCell>
                                            <TableCell>{product.stock_quantity || 0}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        
                        <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-700">
                                <strong>{productsToSync.length}</strong> produit(s) seront synchronisés vers Shopify.
                                Les produits existants seront mis à jour, les nouveaux seront créés.
                            </p>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSyncDialog(false)} disabled={syncing}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleSyncToShopify} 
                            disabled={syncing}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {syncing ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Synchronisation...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Confirmer la synchronisation
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
